import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { API_BASE_URL, clearManualAuthContext, getAuthContext, hasResolvableAuth } from "../services/authContext";
import { loginAuthService } from "../services/loginAuthService";
import juricloudLogo from "../../../assets/brand-icon.png";
import {
  disconnectChatSocket,
  getChatSocket,
  unwrapSocketPayload,
  waitForSocketConnect,
} from "../services/chatSocket";
import { jurionChatService } from "../services/jurionChatService";
import { aiService } from "../services/aiService";
import { generateDocumentTypes, generateDocumentTypeFields } from "../lib/GenerateDocumentConfig";
import { Citation, DraftPrompt, DraftReferenceDocument, RedlineSuggestion } from "../types";
import { wordAdapter } from "../services/wordAdapter";
import { parseCitations } from "../services/citationParser";
import { parseSuggestionsBlock, redlineService } from "../services/redlineService";
import { htmlToMarkdown } from "../services/htmlToText";
import { PromptLibraryDialog } from "./PromptLibraryDialog";

type Tab = "review" | "draft" | "redline";

type EmittedSuggestion = RedlineSuggestion & {
  /** Outcome after we tried to insert it into Word as a tracked change. */
  applyState: "applying" | "applied" | "missing";
};

type ThreadMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  visible?: string;
  citations?: Citation[];
  emittedSuggestions?: EmittedSuggestion[];
};

type Starter = { title: string; subtitle: string; prompt: string };

type DraftFieldType = "string" | "number" | "boolean" | "date";
type DraftCustomField = { name: string; type: DraftFieldType; value: string };

const REVIEW_STARTERS: Starter[] = [
  { title: "Summarize this contract", subtitle: "Parties, term, key risk", prompt: "Summarize this document — list the parties, key terms, and primary risks." },
  { title: "Find indemnity & limitation caps", subtitle: "With clause references", prompt: "List all indemnity provisions and limitation of liability caps in this document, with clause references." },
  { title: "Cite authority for clause 4.2", subtitle: "Jurisdiction-aware", prompt: "Cite supporting Indian legal authority for clause 4.2 of this document." },
  { title: "Compare to our standard NDA", subtitle: "Highlight deviations", prompt: "Compare this NDA to a standard mutual NDA and highlight deviations." },
];

const DRAFT_LANGUAGES = ["English", "Hindi", "Marathi", "Gujarati", "Tamil", "Telugu", "Bengali", "Kannada", "Malayalam"];
const DRAFTING_MODES = ["General", "Essential", "Robust", "Defensible"];

// Inline SVG icons
const IconSearch = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const IconPen = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
);
const IconLines = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="4" y1="6" x2="20" y2="6" />
    <line x1="4" y1="12" x2="14" y2="12" />
    <line x1="4" y1="18" x2="18" y2="18" />
  </svg>
);
const IconSend = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);
const IconPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const IconArrowLeft = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);
const IconSparkle = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2l1.6 6.4L20 10l-6.4 1.6L12 18l-1.6-6.4L4 10l6.4-1.6L12 2z" />
  </svg>
);
/** Prompt library (matches LegalAgentChat Prompt Library entry) */
const IconBookOpen = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
  </svg>
);
const IconSun = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
  </svg>
);
const IconMoon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const APP_VERSION = "v1.0.0";
/** Logo for Review and Redline empty states (bundled via assets/markonly.svg) */
const MARKONLY_LOGO_URL = "assets/markonly.svg";

const IconLock = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const IconArrowRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

type AuthScreenProps = {
  email: string;
  password: string;
  loading: boolean;
  error: string;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onSubmit: () => void;
};

function AuthScreen({ email, password, loading, error, onEmailChange, onPasswordChange, onSubmit }: AuthScreenProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(true);

  return (
    <div className="auth-shell">
      <header className="auth-shell-header">
        <div className="brand">
          <img className="brand-logo" src={juricloudLogo} alt="" />
        </div>
        <span className="auth-version">{APP_VERSION}</span>
      </header>

      <main className="auth-body">
        <div className="auth-logo-frame">
          <img src={juricloudLogo} alt="" />
        </div>
        <h1 className="auth-headline">Welcome back</h1>
        <p className="auth-subline">Sign in to bring Jurion into this document.</p>

        <form
          className="auth-form"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit();
          }}
        >
          <div className="auth-field">
            <label htmlFor="auth-email" className="auth-label">WORK EMAIL</label>
            <input
              id="auth-email"
              type="email"
              placeholder="you@firm.com"
              value={email}
              onChange={(event) => onEmailChange(event.target.value)}
              autoComplete="email"
              className="auth-input"
            />
          </div>

          <div className="auth-field">
            <div className="auth-label-row">
              <label htmlFor="auth-password" className="auth-label">PASSWORD</label>
              <a className="auth-inline-link" href="https://juricloud.co" target="_blank" rel="noreferrer">
                Forgot?
              </a>
            </div>
            <div className="auth-input-wrap">
              <input
                id="auth-password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(event) => onPasswordChange(event.target.value)}
                autoComplete="current-password"
                className="auth-input"
              />
              <button
                type="button"
                className="auth-input-action"
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? "HIDE" : "SHOW"}
              </button>
            </div>
          </div>

          <label className="auth-checkbox">
            <input
              type="checkbox"
              checked={keepSignedIn}
              onChange={(event) => setKeepSignedIn(event.target.checked)}
            />
            <span className="auth-checkbox-box" aria-hidden="true">
              {keepSignedIn && (
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </span>
            <span>Keep me signed in on this device</span>
          </label>

          <button
            type="submit"
            className="auth-submit-btn"
            disabled={loading || !email.trim() || !password.trim()}
          >
            <span>{loading ? "Signing in…" : "Sign in"}</span>
            {!loading && <IconArrowRight />}
          </button>

          {error && <p className="error">{error}</p>}

          <p className="auth-request">
            New to Juricloud?{" "}
            <a href="https://juricloud.co" target="_blank" rel="noreferrer" className="auth-inline-link strong">
              Request access
            </a>
          </p>
        </form>
      </main>

      <footer className="auth-shell-footer">
        <IconLock />
        <span>Protected by SSO · SOC 2 Type II · Your documents stay in your tenant</span>
      </footer>
    </div>
  );
}

export function App() {
  // Auth + global UI state
  const [error, setError] = useState("");
  /** Which tab an authenticated-session error belongs to (login errors ignore this). */
  const [errorTab, setErrorTab] = useState<Tab | null>(null);
  const clearAppError = () => {
    setError("");
    setErrorTab(null);
  };
  const [loading, setLoading] = useState(false);
  const [authReady, setAuthReady] = useState(() => hasResolvableAuth());
  const [baseUrlInput] = useState(() => API_BASE_URL);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [, setUserEmail] = useState("");
  const [activeTab, setActiveTab] = useState<Tab>("review");
  const [theme, setTheme] = useState<"light" | "dark">(() => {
    const saved = localStorage.getItem("juricloud_theme") as "light" | "dark" | null;
    if (saved === "light" || saved === "dark") return saved;
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("juricloud_theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme((cur) => (cur === "light" ? "dark" : "light"));

  // Persist the raw HTML for Word insertion AND a markdown-converted version for display.
  const applyGeneratedDraft = (raw: string) => {
    setDraftGeneratedHtml(raw);
    setDraftGeneratedContent(htmlToMarkdown(raw));
  };

  // Word context (selection vs whole document)
  const [reviewContextText, setReviewContextText] = useState("");
  const [reviewContextSource, setReviewContextSource] = useState<"selection" | "document" | "none">("none");

  // Review chat state
  const [chatId, setChatId] = useState("");
  const [prompt, setPrompt] = useState("");
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [isReplying, setIsReplying] = useState(false);
  const [isReviewPromptLibraryOpen, setIsReviewPromptLibraryOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const contentScrollRef = useRef<HTMLDivElement | null>(null);
  const streamingAssistantIdRef = useRef<string | null>(null);
  const activeStreamIdRef = useRef<string | null>(null);

  // Draft form state
  const [draftTitle, setDraftTitle] = useState("");
  const [draftDocumentType, setDraftDocumentType] = useState("");
  const [draftCategory, setDraftCategory] = useState("");
  const [draftLanguage, setDraftLanguage] = useState("English");
  const [draftMode, setDraftMode] = useState("General");
  const [draftDescription, setDraftDescription] = useState("");
  const [draftSystemPrompt, setDraftSystemPrompt] = useState("");
  const [draftReferenceDoc, setDraftReferenceDoc] = useState<DraftReferenceDocument | null>(null);
  const [draftReferenceSearch, setDraftReferenceSearch] = useState("");
  const [referenceDocs, setReferenceDocs] = useState<DraftReferenceDocument[]>([]);
  const [draftFields, setDraftFields] = useState<DraftCustomField[]>([]);
  const [savedPrompts, setSavedPrompts] = useState<DraftPrompt[]>([]);
  const [savePromptTitle, setSavePromptTitle] = useState("");
  const [activePromptId, setActivePromptId] = useState("");
  const [isGeneratingDraft, setIsGeneratingDraft] = useState(false);
  const [draftGeneratedContent, setDraftGeneratedContent] = useState("");
  const [draftGeneratedHtml, setDraftGeneratedHtml] = useState("");
  const [pendingHistoryId, setPendingHistoryId] = useState<string>("");
  const [newFieldName, setNewFieldName] = useState("");
  const [newFieldType, setNewFieldType] = useState<DraftFieldType>("string");
  const [newFieldValue, setNewFieldValue] = useState("");

  // Redline state
  const [redlineSuggestions, setRedlineSuggestions] = useState<RedlineSuggestion[]>([]);
  const [expandedRedlineId, setExpandedRedlineId] = useState<string>("");
  const [isRequestingRedlines, setIsRequestingRedlines] = useState(false);

  // ───────────────────────────────────────────────────────────────────────
  // Effects
  // ───────────────────────────────────────────────────────────────────────

  const refreshReviewContext = async () => {
    try {
      const selected = (await wordAdapter.getSelectedText()).trim();
      if (selected) {
        setReviewContextText(selected);
        setReviewContextSource("selection");
        return;
      }
      const bodyText = (await wordAdapter.getDocumentText()).trim();
      if (bodyText) {
        setReviewContextText(bodyText);
        setReviewContextSource("document");
        return;
      }
      setReviewContextText("");
      setReviewContextSource("none");
    } catch {
      setReviewContextText("");
      setReviewContextSource("none");
    }
  };

  useEffect(() => {
    if (!authReady) return undefined;
    refreshReviewContext();
    const timer = window.setInterval(refreshReviewContext, 2000);
    return () => window.clearInterval(timer);
  }, [authReady]);

  // Review: Socket.IO streaming (same events as juricloud-fe DocumentAnalysisLegalChat)
  useEffect(() => {
    if (!authReady) return undefined;

    let sock: ReturnType<typeof getChatSocket>;
    try {
      sock = getChatSocket();
    } catch {
      return undefined;
    }

    const onStreamStart = (raw: unknown) => {
      const data = unwrapSocketPayload(raw);
      activeStreamIdRef.current = data.streamId;
    };

    const onStreamChunk = (raw: unknown) => {
      const data = unwrapSocketPayload(raw);
      const assistantId = streamingAssistantIdRef.current;
      if (!assistantId) return;
      if (!activeStreamIdRef.current) {
        activeStreamIdRef.current = data.streamId;
      }
      if (data.streamId !== activeStreamIdRef.current) return;
      setMessages((prev) =>
        prev.map((m) => (m.id === assistantId ? { ...m, content: data.content ?? "" } : m))
      );
    };

    const onStreamComplete = (raw: unknown) => {
      const data = unwrapSocketPayload(raw);
      if (data.streamId !== activeStreamIdRef.current) return;
      activeStreamIdRef.current = null;
      const assistantId = streamingAssistantIdRef.current;
      streamingAssistantIdRef.current = null;
      setIsReplying(false);
      if (!assistantId) return;

      let suggestionsToApply: EmittedSuggestion[] = [];
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== assistantId) return m;
          const replyText = m.content || "";
          const { visible: afterCitations, citations } = parseCitations(replyText);
          const { visible: cleaned, suggestions } = parseSuggestionsBlock(afterCitations, assistantId);
          if (suggestions.length === 0) {
            return { ...m, content: replyText, visible: afterCitations, citations };
          }
          const emitted: EmittedSuggestion[] = suggestions.map((s) => ({
            ...s,
            applyState: "applying",
          }));
          suggestionsToApply = emitted;
          return { ...m, content: replyText, visible: cleaned, citations, emittedSuggestions: emitted };
        })
      );

      if (suggestionsToApply.length > 0) {
        void applyEmittedSuggestions(assistantId, suggestionsToApply);
      }
    };

    const onStreamError = (raw: unknown) => {
      const data = unwrapSocketPayload(raw);
      if (data.streamId !== activeStreamIdRef.current) return;
      activeStreamIdRef.current = null;
      const assistantId = streamingAssistantIdRef.current;
      streamingAssistantIdRef.current = null;
      setIsReplying(false);
      if (assistantId) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? { ...m, content: "Sorry, something went wrong. Please try again." }
              : m
          )
        );
      }
    };

    const onStreamStopped = (raw: unknown) => {
      const data = unwrapSocketPayload(raw);
      if (data.streamId !== activeStreamIdRef.current) return;
      activeStreamIdRef.current = null;
      streamingAssistantIdRef.current = null;
      setIsReplying(false);
    };

    sock.on("chatbot:stream-start", onStreamStart);
    sock.on("chatbot:stream-chunk", onStreamChunk);
    sock.on("chatbot:stream-complete", onStreamComplete);
    sock.on("chatbot:stream-error", onStreamError);
    sock.on("chatbot:stream-stopped", onStreamStopped);

    return () => {
      sock.off("chatbot:stream-start", onStreamStart);
      sock.off("chatbot:stream-chunk", onStreamChunk);
      sock.off("chatbot:stream-complete", onStreamComplete);
      sock.off("chatbot:stream-error", onStreamError);
      sock.off("chatbot:stream-stopped", onStreamStopped);
    };
  }, [authReady]);

  // Review: keep latest messages / typing indicator in view
  useEffect(() => {
    if (activeTab !== "review") return;
    if (messages.length === 0 && !isReplying) return;
    const el = contentScrollRef.current;
    if (!el) return;
    const scrollEnd = () => {
      el.scrollTop = el.scrollHeight;
    };
    requestAnimationFrame(() => requestAnimationFrame(scrollEnd));
  }, [activeTab, messages, isReplying]);

  // Auto-load saved prompts when document type changes (Draft tab)
  useEffect(() => {
    if (!authReady || activeTab !== "draft" || !draftDocumentType) {
      setSavedPrompts([]);
      setActivePromptId("");
      return;
    }
    const requiredFields = (generateDocumentTypeFields[draftDocumentType] || []).map((field) => ({
      name: field.name,
      type: field.type as DraftFieldType,
      value: String(field.value ?? ""),
    }));
    setDraftFields(requiredFields);

    aiService
      .getDocumentPrompts(draftDocumentType)
      .then((prompts) => setSavedPrompts(prompts))
      .catch(() => setSavedPrompts([]));
  }, [authReady, activeTab, draftDocumentType]);

  // Auto-load reference documents (Draft tab)
  useEffect(() => {
    if (!authReady || activeTab !== "draft") return;
    aiService
      .getReferenceDocuments(draftReferenceSearch)
      .then((docs) => setReferenceDocs(docs))
      .catch(() => setReferenceDocs([]));
  }, [authReady, activeTab, draftReferenceSearch]);

  // Poll the generation-history endpoint until the queued draft is complete.
  // Backend kicks the ALA pipeline in the background and returns a historyId
  // immediately; we have to fetch the entry to pick up the final document.
  useEffect(() => {
    if (!pendingHistoryId) return undefined;
    let cancelled = false;
    const startedAt = Date.now();
    const TIMEOUT_MS = 10 * 60 * 1000; // give up after 10 minutes

    const tick = async () => {
      if (cancelled) return;
      try {
        const result = await aiService.checkGenerationStatus(pendingHistoryId);
        if (cancelled) return;
        const status = (result.status || "").toLowerCase();
        const isDone = ["completed", "complete", "success", "ready"].includes(status);
        const isFailed = ["failed", "error"].includes(status);
        if (isDone && result.document) {
          applyGeneratedDraft(result.document);
          setPendingHistoryId("");
          return;
        }
        if (isDone && !result.document) {
          // Status flipped to done but the content field was empty — surface raw entry so
          // we can see what shape the backend used.
          // eslint-disable-next-line no-console
          console.warn("[draft] generation marked complete but no document field found", result.raw);
          setError("Generation completed but no content was returned. Check JuriCloud document history.");
          setErrorTab("draft");
          setPendingHistoryId("");
          return;
        }
        if (isFailed) {
          setError(`Document generation failed: ${result.raw?.errorMessage || "unknown error"}`);
          setErrorTab("draft");
          setPendingHistoryId("");
          return;
        }
      } catch (err) {
        // transient network errors are expected during a 3–5 min poll; ignore and retry
      }
      if (Date.now() - startedAt > TIMEOUT_MS) {
        setError("Document generation timed out after 10 minutes. Check JuriCloud document history.");
        setErrorTab("draft");
        setPendingHistoryId("");
      }
    };

    // Fire once immediately, then on an interval.
    tick();
    const interval = window.setInterval(tick, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [pendingHistoryId]);

  // ───────────────────────────────────────────────────────────────────────
  // Handlers
  // ───────────────────────────────────────────────────────────────────────

  const onLogin = async () => {
    clearAppError();
    setLoading(true);
    try {
      if (!baseUrlInput.trim() || !email.trim() || !password.trim()) {
        throw new Error("Email and password are required.");
      }
      const result = await loginAuthService.loginWithPassword(baseUrlInput.trim(), email.trim(), password);
      setUserEmail(result.email);
      setAuthReady(true);
      setPassword("");
    } catch (err: any) {
      setError(err?.message || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  const onLogout = () => {
    disconnectChatSocket();
    clearAppError();
    clearManualAuthContext();
    setAuthReady(false);
    setPassword("");
    setMessages([]);
    setChatId("");
    setRedlineSuggestions([]);
    setDraftGeneratedContent("");
    setDraftGeneratedHtml("");
  };

  const onNewChat = () => {
    setChatId("");
    setMessages([]);
    setRedlineSuggestions([]);
    setExpandedRedlineId("");
    setDraftGeneratedContent("");
    setDraftGeneratedHtml("");
    setPendingHistoryId("");
    clearAppError();
    setPrompt("");
  };

  // Run the AI-emitted suggestions through wordAdapter.applySuggestionsBatch.
  // Updates the message's chip statuses in place so the UI reflects success
  // ("applied" — now a tracked change in Word) or failure ("missing" — verbatim
  // match not found in the doc).
  const applyEmittedSuggestions = async (messageId: string, suggestions: EmittedSuggestion[]) => {
    try {
      const outcome = await wordAdapter.applySuggestionsBatch(
        suggestions.map((s) => ({
          id: s.id,
          originalText: s.originalText,
          suggestedText: s.suggestedText,
        }))
      );
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== messageId || !m.emittedSuggestions) return m;
          return {
            ...m,
            emittedSuggestions: m.emittedSuggestions.map((s) => ({
              ...s,
              applyState: outcome[s.id] === "applied" ? "applied" : "missing",
            })),
          };
        })
      );
    } catch (err: any) {
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== messageId || !m.emittedSuggestions) return m;
          return {
            ...m,
            emittedSuggestions: m.emittedSuggestions.map((s) => ({ ...s, applyState: "missing" })),
          };
        })
      );
      setError(err?.message || "Failed to apply tracked changes to the document.");
      setErrorTab("review");
    }
  };

  // Click handler on a suggestion chip: locate the inserted text in the doc
  // and select/scroll to it so the user can review it in Word's native pane.
  const onJumpToSuggestion = async (suggestion: EmittedSuggestion) => {
    try {
      const target = suggestion.suggestedText.trim() || suggestion.originalText.trim();
      if (!target) return;
      const ok = await wordAdapter.highlightExcerpt(target);
      if (!ok) {
        setError("Could not locate the suggestion in the document.");
        setErrorTab("review");
      }
    } catch (err: any) {
      setError(err?.message || "Failed to jump to suggestion.");
      setErrorTab("review");
    }
  };

  // Review chat — primary path: Socket.IO streaming (same as juricloud-fe); fallback: REST
  const sendReviewPrompt = async (content: string) => {
    const userMessageId = `user-${Date.now()}`;
    const assistantMessageId = `assistant-${Date.now()}`;
    streamingAssistantIdRef.current = assistantMessageId;
    activeStreamIdRef.current = null;

    const trimmedContext = reviewContextText.trim();
    const contextPrefix = trimmedContext
      ? `\n\nContext Source: ${
          reviewContextSource === "selection" ? "Selected Text" : "Whole Document"
        }\nContext:\n${trimmedContext.slice(0, 12000)}`
      : "";
    const fullMessage = `${content}${contextPrefix}`;
    const metadata = {
      source: "word_addin",
      chatMode: "drafting",
      contextSource: reviewContextSource,
      contextLength: trimmedContext.length,
    };

    setMessages((cur) => [...cur, { id: userMessageId, role: "user", content }]);
    setMessages((cur) => [...cur, { id: assistantMessageId, role: "assistant", content: "" }]);
    setIsReplying(true);

    let resolvedChatId: string | undefined = chatId || undefined;
    try {
      if (!resolvedChatId) {
        resolvedChatId = await jurionChatService.createResearchChat();
        setChatId(resolvedChatId);
      }

      const auth = getAuthContext();
      const socket = getChatSocket();
      await waitForSocketConnect(socket);

      socket.emit("chatbot:send-message", {
        chatId: resolvedChatId,
        message: fullMessage,
        tenantId: auth.tenantId,
        metadata,
      });
    } catch (err: any) {
      try {
        if (!resolvedChatId) {
          resolvedChatId = await jurionChatService.createResearchChat();
          setChatId(resolvedChatId);
        }
        const reply = await jurionChatService.sendMessage(resolvedChatId, fullMessage, {
          metadata,
        });
        const replyText = reply || "I could not generate a response. Please try again.";
        const { visible: afterCitations, citations } = parseCitations(replyText);
        const { visible: cleaned, suggestions } = parseSuggestionsBlock(afterCitations, assistantMessageId);
        const emitted: EmittedSuggestion[] = suggestions.map((s) => ({ ...s, applyState: "applying" }));
        setMessages((cur) =>
          cur.map((m) =>
            m.id === assistantMessageId
              ? {
                  ...m,
                  content: replyText,
                  visible: emitted.length > 0 ? cleaned : afterCitations,
                  citations,
                  emittedSuggestions: emitted.length > 0 ? emitted : undefined,
                }
              : m
          )
        );
        if (emitted.length > 0) {
          void applyEmittedSuggestions(assistantMessageId, emitted);
        }
      } catch (e2: any) {
        setMessages((cur) => cur.filter((m) => m.id !== assistantMessageId));
        setError(e2?.message || err?.message || "Failed to get response.");
        setErrorTab("review");
      } finally {
        setIsReplying(false);
        streamingAssistantIdRef.current = null;
        activeStreamIdRef.current = null;
      }
    }
  };

  const onSendPrompt = async () => {
    const content = prompt.trim();
    if (!content || isReplying) return;
    clearAppError();
    setPrompt("");
    await sendReviewPrompt(content);
  };

  const onStarterClick = (starter: Starter) => {
    setPrompt(starter.prompt);
    textareaRef.current?.focus();
  };

  const onComposerKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      onSendPrompt();
    }
  };

  // Citations
  const onCitationClick = async (citation: Citation) => {
    clearAppError();
    try {
      const found = await wordAdapter.highlightExcerpt(citation.excerpt);
      if (!found) {
        setError(`Could not locate citation excerpt: "${citation.excerpt.slice(0, 60)}..."`);
        setErrorTab("review");
      }
    } catch (err: any) {
      setError(err?.message || "Failed to highlight citation.");
      setErrorTab("review");
    }
  };

  const onClearHighlights = async () => {
    clearAppError();
    try {
      await wordAdapter.clearHighlights();
    } catch (err: any) {
      setError(err?.message || "Failed to clear highlights.");
      setErrorTab("review");
    }
  };

  // Draft form
  const addDraftField = () => {
    if (!newFieldName.trim()) return;
    setDraftFields((prev) => [...prev, { name: newFieldName.trim(), type: newFieldType, value: newFieldValue }]);
    setNewFieldName("");
    setNewFieldType("string");
    setNewFieldValue("");
  };

  const updateDraftField = (index: number, key: "name" | "type" | "value", value: string) => {
    setDraftFields((prev) =>
      prev.map((field, i) => (i === index ? { ...field, [key]: value } : field))
    );
  };

  const removeDraftField = (index: number) => {
    setDraftFields((prev) => prev.filter((_, i) => i !== index));
  };

  const onApplySavedPrompt = (promptItem: DraftPrompt) => {
    setDraftSystemPrompt(promptItem.content);
    setActivePromptId(promptItem.id);
  };

  const onSavePrompt = async () => {
    if (!draftDocumentType || !savePromptTitle.trim() || !draftSystemPrompt.trim()) return;
    await aiService.saveDocumentPrompt({
      title: savePromptTitle.trim(),
      content: draftSystemPrompt,
      documentType: draftDocumentType,
    });
    const prompts = await aiService.getDocumentPrompts(draftDocumentType);
    setSavedPrompts(prompts);
    setSavePromptTitle("");
  };

  const onDeletePrompt = async (promptId: string) => {
    if (!draftDocumentType) return;
    await aiService.deleteDocumentPrompt(promptId);
    const prompts = await aiService.getDocumentPrompts(draftDocumentType);
    setSavedPrompts(prompts);
    if (activePromptId === promptId) setActivePromptId("");
  };

  const onGenerateDraftDocument = async () => {
    if (!draftTitle.trim() || !draftDocumentType || !draftLanguage) {
      setError("Document title, document type, and language are required.");
      setErrorTab("draft");
      return;
    }
    setIsGeneratingDraft(true);
    clearAppError();
    try {
      const response = await aiService.generateDocument({
        title: draftTitle.trim(),
        documentType: draftDocumentType,
        category: draftCategory.trim() || draftDocumentType,
        language: draftLanguage,
        description: draftDescription || "",
        additionalDetails: draftFields,
        attachment: draftReferenceDoc || undefined,
        attachedId: draftReferenceDoc?.id,
        additionalPrompt: draftSystemPrompt.trim() || undefined,
        draftingMode: draftMode,
      });
      if (response.pendingHistoryId) {
        setPendingHistoryId(response.pendingHistoryId);
        setDraftGeneratedHtml("");
        setDraftGeneratedContent(
          response.pendingMessage ||
            "Document generation started. This usually takes 3–5 minutes. The result will appear here automatically."
        );
      } else if (response.document) {
        applyGeneratedDraft(response.document);
      } else {
        setDraftGeneratedHtml("");
        setDraftGeneratedContent("Draft generated but content was empty. Open document history for full output.");
      }
    } catch (err: any) {
      setError(err?.message || "Failed to generate document.");
      setErrorTab("draft");
    } finally {
      setIsGeneratingDraft(false);
    }
  };

  const onResetDraftForm = () => {
    clearAppError();
    setDraftTitle("");
    setDraftDocumentType("");
    setDraftCategory("");
    setDraftLanguage("English");
    setDraftMode("General");
    setDraftDescription("");
    setDraftSystemPrompt("");
    setDraftReferenceDoc(null);
    setDraftReferenceSearch("");
    setDraftFields([]);
    setDraftGeneratedContent("");
    setDraftGeneratedHtml("");
    setActivePromptId("");
    setPendingHistoryId("");
  };

  const onInsertDraftAtCursor = async () => {
    const payload = draftGeneratedHtml || draftGeneratedContent;
    if (!payload.trim()) return;
    clearAppError();
    try {
      await wordAdapter.insertDraftAtCursor(payload);
    } catch (err: any) {
      setError(err?.message || "Failed to insert draft at cursor.");
      setErrorTab("draft");
    }
  };

  const onReplaceDocumentWithDraft = async () => {
    const payload = draftGeneratedHtml || draftGeneratedContent;
    if (!payload.trim()) return;
    clearAppError();
    try {
      await wordAdapter.replaceDocumentWithDraft(payload);
    } catch (err: any) {
      setError(err?.message || "Failed to replace document.");
      setErrorTab("draft");
    }
  };

  // Redline
  const onStartRedline = async () => {
    if (!reviewContextText.trim()) {
      setError("Open a Word document with content (or select text) before starting a redline.");
      setErrorTab("redline");
      return;
    }
    setIsRequestingRedlines(true);
    clearAppError();
    try {
      const redlineChatId = await jurionChatService.createResearchChat();
      const suggestions = await redlineService.requestRedlines(redlineChatId, reviewContextText);
      setRedlineSuggestions(suggestions);
      if (!suggestions.length) {
        setError("No redline suggestions returned. Try refining your selection or running again.");
        setErrorTab("redline");
      } else {
        setExpandedRedlineId(suggestions[0].id);
      }
    } catch (err: any) {
      setError(err?.message || "Failed to fetch redlines.");
      setErrorTab("redline");
    } finally {
      setIsRequestingRedlines(false);
    }
  };

  const onAcceptRedline = async (suggestion: RedlineSuggestion) => {
    clearAppError();
    try {
      await wordAdapter.applySuggestion(suggestion);
      setRedlineSuggestions((cur) =>
        cur.map((item) => (item.id === suggestion.id ? { ...item, status: "accepted" } : item))
      );
    } catch (err: any) {
      setError(err?.message || "Failed to apply redline. The original text may have changed.");
      setErrorTab("redline");
      setRedlineSuggestions((cur) =>
        cur.map((item) => (item.id === suggestion.id ? { ...item, status: "missing" } : item))
      );
    }
  };

  const onIgnoreRedline = (suggestion: RedlineSuggestion) => {
    setRedlineSuggestions((cur) =>
      cur.map((item) => (item.id === suggestion.id ? { ...item, status: "ignored" } : item))
    );
  };

  const onClearRedlines = () => {
    setRedlineSuggestions([]);
    setExpandedRedlineId("");
  };

  // ───────────────────────────────────────────────────────────────────────
  // Render
  // ───────────────────────────────────────────────────────────────────────

  if (!authReady) {
    return <AuthScreen
      email={email}
      password={password}
      loading={loading}
      error={error}
      onEmailChange={setEmail}
      onPasswordChange={setPassword}
      onSubmit={onLogin}
    />;
  }

  const docLinked = reviewContextSource !== "none";

  return (
    <div className="app">
      <header className="app-header">
        <div className="brand">
          <img className="brand-logo" src={juricloudLogo} alt="" />
        </div>
        <div className="header-actions">
          <button className="icon-btn" title="New chat" aria-label="New chat" onClick={onNewChat}>
            <IconPlus />
          </button>
          <button
            className="icon-btn"
            title={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
            aria-label="Toggle theme"
            onClick={toggleTheme}
          >
            {theme === "light" ? <IconMoon /> : <IconSun />}
          </button>
          <button className="icon-btn" title="Sign out" aria-label="Sign out" onClick={onLogout}>
            <IconArrowLeft />
          </button>
        </div>
      </header>

      <nav className="tab-bar">
        <button
          type="button"
          className={`tab ${activeTab === "review" ? "tab-active" : ""}`}
          onClick={() => setActiveTab("review")}
        >
          <IconSearch />
          <span>Review</span>
        </button>
        <button
          type="button"
          className={`tab ${activeTab === "draft" ? "tab-active" : ""}`}
          onClick={() => setActiveTab("draft")}
        >
          <IconPen />
          <span>Draft</span>
        </button>
        <button
          type="button"
          className={`tab ${activeTab === "redline" ? "tab-active" : ""}`}
          onClick={() => setActiveTab("redline")}
        >
          <IconLines />
          <span>Redline</span>
        </button>
      </nav>

      <main ref={contentScrollRef} className="content">
        {/* ── REVIEW TAB ── */}
        {activeTab === "review" && messages.length === 0 && !isReplying && (
          <div className="empty-state">
            <div className="empty-logo-frame">
              <img src={MARKONLY_LOGO_URL} alt="" />
            </div>
            <h1 className="empty-headline">Read. Reason. Cite.</h1>
            <p className="empty-subline">
              Surface risks, summarize clauses, and pull authority — grounded in your document.
            </p>
            <div className="starter-section">
              <span className="starter-label">TRY</span>
              <div className="starter-grid">
                {REVIEW_STARTERS.map((starter) => (
                  <button
                    key={starter.title}
                    type="button"
                    className="starter-card"
                    onClick={() => onStarterClick(starter)}
                  >
                    <div className="starter-card-title">{starter.title}</div>
                    <div className="starter-card-subtitle">{starter.subtitle}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="empty-footer">
              <IconSparkle />
              <span>Grounded in your open document · cites every claim</span>
            </div>
          </div>
        )}

        {activeTab === "review" && (messages.length > 0 || isReplying) && (
          <div className="thread">
            {messages.map((message) => {
              // Hide both a fully-closed suggestions block (parsed on stream-complete) and
              // a mid-stream one (still being emitted) so the bubble only ever shows prose.
              const stripFenceForDisplay = (text: string): string => {
                const fenceIdx = text.search(/```(?:suggestions|json)\b/i);
                return fenceIdx === -1 ? text : text.slice(0, fenceIdx).trimEnd();
              };
              if (message.role === "assistant") {
                const bodyText =
                  message.visible !== undefined && message.visible !== null
                    ? String(message.visible)
                    : message.content || "";
                const displayBody = stripFenceForDisplay(bodyText);
                if (!displayBody.trim() && !message.citations?.length && !message.emittedSuggestions?.length) {
                  return null;
                }
              }
              const rawVisible = message.visible !== undefined ? message.visible : message.content;
              const displayText = message.role === "assistant" ? stripFenceForDisplay(rawVisible || "") : rawVisible || "";
              return (
                <article key={message.id} className={`msg msg-${message.role}`}>
                  <div className="msg-bubble">
                    {message.role === "assistant" ? (
                      <div className="markdown-body">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{displayText}</ReactMarkdown>
                      </div>
                    ) : (
                      <p>{message.content}</p>
                    )}
                    {message.role === "assistant" && !!message.emittedSuggestions?.length && (
                      <div className="suggestion-list">
                        <div className="suggestion-list-header">
                          <IconSparkle />
                          <span>
                            {message.emittedSuggestions.every((s) => s.applyState === "applying")
                              ? "Inserting suggestions as tracked changes…"
                              : `${message.emittedSuggestions.filter((s) => s.applyState === "applied").length} of ${message.emittedSuggestions.length} suggestion${message.emittedSuggestions.length === 1 ? "" : "s"} inserted into the document`}
                          </span>
                        </div>
                        {message.emittedSuggestions.map((s) => (
                          <button
                            key={s.id}
                            type="button"
                            className={`suggestion-chip suggestion-${s.applyState}`}
                            onClick={() => onJumpToSuggestion(s)}
                            title={s.reason || s.title}
                          >
                            <span className={`suggestion-dot suggestion-dot-${s.applyState}`} />
                            <span className="suggestion-title">{s.title}</span>
                            <span className="suggestion-state">
                              {s.applyState === "applied"
                                ? "Tracked"
                                : s.applyState === "applying"
                                  ? "Inserting…"
                                  : "Not found"}
                            </span>
                          </button>
                        ))}
                        <p className="suggestion-footnote">
                          Each tracked change is now in your document — accept or reject from Word&apos;s Review tab. Click a chip above to jump to it.
                        </p>
                      </div>
                    )}
                    {message.role === "assistant" && !!message.citations?.length && (
                      <div className="citation-list">
                        <div className="citation-list-header">
                          <span>Citations</span>
                          <button type="button" className="link-btn" onClick={onClearHighlights}>
                            Clear highlights
                          </button>
                        </div>
                        {message.citations.map((citation) => (
                          <button
                            key={`${message.id}-c-${citation.id}`}
                            type="button"
                            className="citation-chip"
                            onClick={() => onCitationClick(citation)}
                            title="Click to highlight in document"
                          >
                            <span className="citation-num">[{citation.id}]</span>
                            <span className="citation-doc">{citation.documentName || `Doc ${citation.tid}`}</span>
                            {citation.page > 0 && <span className="citation-page">p.{citation.page}</span>}
                            <span className="citation-excerpt">{citation.excerpt}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
            {isReplying &&
              !messages.some((m) => {
                if (m.role !== "assistant") return false;
                const t =
                  m.visible !== undefined && m.visible !== null ? String(m.visible) : m.content || "";
                return !!t.trim() || !!(m.citations && m.citations.length);
              }) && (
                <article className="msg msg-assistant">
                  <div className="msg-bubble">
                    <div className="typing-dots">
                      <span />
                      <span />
                      <span />
                    </div>
                  </div>
                </article>
              )}
          </div>
        )}

        {/* ── DRAFT TAB (form, no chat) ── */}
        {activeTab === "draft" && (
          <div className="form-pane">
            <div className="form-card">
              <h3 className="form-card-title">Basic Information</h3>
              <p className="form-card-help">Provide essential details for your draft document.</p>
              <div className="form-grid">
                <label className="form-label">
                  Document Title *
                  <input
                    value={draftTitle}
                    onChange={(event) => setDraftTitle(event.target.value)}
                    placeholder="e.g., Employment Agreement between Company X and John Doe"
                  />
                </label>
                <label className="form-label">
                  Document Type *
                  <select value={draftDocumentType} onChange={(event) => setDraftDocumentType(event.target.value)}>
                    <option value="">Select document type</option>
                    {generateDocumentTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="form-label">
                  Category
                  <input value={draftCategory} onChange={(event) => setDraftCategory(event.target.value)} placeholder="Optional category" />
                </label>
                <label className="form-label">
                  Language *
                  <select value={draftLanguage} onChange={(event) => setDraftLanguage(event.target.value)}>
                    {DRAFT_LANGUAGES.map((language) => (
                      <option key={language} value={language}>
                        {language}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="form-label">
                  Drafting Mode *
                  <select value={draftMode} onChange={(event) => setDraftMode(event.target.value)}>
                    {DRAFTING_MODES.map((mode) => (
                      <option key={mode} value={mode}>
                        {mode}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="form-label form-label-block">
                Description
                <textarea
                  rows={4}
                  value={draftDescription}
                  onChange={(event) => setDraftDescription(event.target.value)}
                  placeholder="Describe what this document should contain…"
                />
              </label>
            </div>

            {draftDocumentType && (
              <div className="form-card">
                <h3 className="form-card-title">Rules (System Prompt)</h3>
                <p className="form-card-help">Set drafting rules, tone, exclusions, and jurisdiction constraints.</p>
                <div className="form-prompts-toolbar">
                  <label className="form-label form-label-compact">
                    Saved Prompt Name
                    <input
                      value={savePromptTitle}
                      onChange={(event) => setSavePromptTitle(event.target.value)}
                      placeholder="e.g., Standard NDA Rules"
                    />
                  </label>
                  <button
                    type="button"
                    className="ghost-btn"
                    onClick={onSavePrompt}
                    disabled={!savePromptTitle.trim() || !draftSystemPrompt.trim()}
                  >
                    Save Prompt
                  </button>
                </div>
                {!!savedPrompts.length && (
                  <div className="form-prompts-list">
                    {savedPrompts.map((promptItem) => (
                      <div key={promptItem.id} className={`form-prompt-chip ${activePromptId === promptItem.id ? "active" : ""}`}>
                        <button type="button" className="form-prompt-main" onClick={() => onApplySavedPrompt(promptItem)}>
                          <span className="form-prompt-title">{promptItem.title}</span>
                          <span className="form-prompt-description">{promptItem.content || "No description provided."}</span>
                        </button>
                        <button type="button" className="form-prompt-delete" onClick={() => onDeletePrompt(promptItem.id)}>
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <label className="form-label form-label-block">
                  System Prompt
                  <textarea
                    rows={4}
                    value={draftSystemPrompt}
                    onChange={(event) => setDraftSystemPrompt(event.target.value)}
                    placeholder="Optional drafting rules and formatting instructions…"
                  />
                </label>
              </div>
            )}

            <div className="form-card">
              <h3 className="form-card-title">Reference Document</h3>
              <p className="form-card-help">Optionally select an existing document to guide format and structure.</p>
              <div className="form-grid">
                <label className="form-label">
                  Search
                  <input
                    value={draftReferenceSearch}
                    onChange={(event) => setDraftReferenceSearch(event.target.value)}
                    placeholder="Search your documents…"
                  />
                </label>
                <label className="form-label">
                  Select
                  <select
                    value={draftReferenceDoc?.id || ""}
                    onChange={(event) => {
                      const selected = referenceDocs.find((doc) => doc.id === event.target.value) || null;
                      setDraftReferenceDoc(selected);
                    }}
                  >
                    <option value="">No reference selected</option>
                    {referenceDocs.map((doc) => (
                      <option key={doc.id} value={doc.id}>
                        {doc.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            <div className="form-card">
              <h3 className="form-card-title">Additional Details</h3>
              <p className="form-card-help">Add required or custom fields for this draft.</p>
              <div className="form-field-row">
                <input value={newFieldName} onChange={(event) => setNewFieldName(event.target.value)} placeholder="Field name" />
                <select value={newFieldType} onChange={(event) => setNewFieldType(event.target.value as DraftFieldType)}>
                  <option value="string">Text</option>
                  <option value="number">Number</option>
                  <option value="boolean">Yes/No</option>
                  <option value="date">Date</option>
                </select>
                <input value={newFieldValue} onChange={(event) => setNewFieldValue(event.target.value)} placeholder="Field value" />
                <button type="button" className="ghost-btn" onClick={addDraftField}>
                  Add
                </button>
              </div>
              {!draftFields.length && <p className="form-empty-line">No additional fields added yet.</p>}
              <div className="form-fields-list">
                {draftFields.map((field, index) => (
                  <div className="form-field-row" key={`${field.name}-${index}`}>
                    <input value={field.name} onChange={(event) => updateDraftField(index, "name", event.target.value)} />
                    <select value={field.type} onChange={(event) => updateDraftField(index, "type", event.target.value)}>
                      <option value="string">Text</option>
                      <option value="number">Number</option>
                      <option value="boolean">Yes/No</option>
                      <option value="date">Date</option>
                    </select>
                    <input value={field.value} onChange={(event) => updateDraftField(index, "value", event.target.value)} />
                    <button type="button" className="ghost-btn" onClick={() => removeDraftField(index)}>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="ghost-btn" onClick={onResetDraftForm}>
                Reset
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={onGenerateDraftDocument}
                disabled={isGeneratingDraft || !!pendingHistoryId}
              >
                {pendingHistoryId
                  ? "Generating in background…"
                  : isGeneratingDraft
                    ? "Submitting..."
                    : "Generate Document"}
              </button>
            </div>

            {draftGeneratedContent && (
              <div className="form-card">
                <h3 className="form-card-title">Generated Output</h3>
                <p className="form-card-help">Insert the generated draft into Word using the buttons below.</p>
                <textarea className="draft-output" value={draftGeneratedContent} readOnly rows={10} />
                <div className="form-actions">
                  <button type="button" className="ghost-btn" onClick={onInsertDraftAtCursor}>
                    Insert at Cursor
                  </button>
                  <button type="button" className="primary-btn" onClick={onReplaceDocumentWithDraft}>
                    Replace Document
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── REDLINE TAB (button-only, no chat) ── */}
        {activeTab === "redline" && !isRequestingRedlines && redlineSuggestions.length === 0 && (
          <div className="empty-state">
            <div className="empty-logo-frame">
              <img src={MARKONLY_LOGO_URL} alt="" />
            </div>
            <h1 className="empty-headline">Edit. Tighten. Track.</h1>
            <p className="empty-subline">
              Generate concrete redline suggestions for your document and apply them as tracked changes in Word.
            </p>
            <button
              type="button"
              className="primary-btn redline-start-btn"
              onClick={onStartRedline}
              disabled={!docLinked}
            >
              {docLinked ? "Start Redlining" : "Open a document to start"}
            </button>
            <div className="empty-footer">
              <IconSparkle />
              <span>
                {reviewContextSource === "selection"
                  ? `Will redline your selected text (${reviewContextText.length} chars)`
                  : reviewContextSource === "document"
                    ? "Will redline the whole document"
                    : "No document detected"}
              </span>
            </div>
          </div>
        )}

        {activeTab === "redline" && isRequestingRedlines && (
          <div className="redline-loading">
            <div className="typing-dots">
              <span />
              <span />
              <span />
            </div>
            <p>Generating redline suggestions for your document. This can take 30–60 seconds.</p>
          </div>
        )}

        {activeTab === "redline" && !!redlineSuggestions.length && (
          <div className="redline-panel">
            <div className="redline-panel-header">
              <span>
                {redlineSuggestions.filter((s) => s.status === "pending").length} pending ·{" "}
                {redlineSuggestions.filter((s) => s.status === "accepted").length} accepted
              </span>
              <div className="redline-panel-actions">
                <button type="button" className="link-btn" onClick={onStartRedline} disabled={isRequestingRedlines}>
                  Re-run
                </button>
                <button type="button" className="link-btn" onClick={onClearRedlines}>
                  Clear all
                </button>
              </div>
            </div>
            {redlineSuggestions.map((suggestion) => (
              <div key={suggestion.id} className={`redline-item status-${suggestion.status}`}>
                <button
                  type="button"
                  className="redline-summary"
                  onClick={() => setExpandedRedlineId((cur) => (cur === suggestion.id ? "" : suggestion.id))}
                >
                  <span className="redline-caret">{expandedRedlineId === suggestion.id ? "▾" : "▸"}</span>
                  <span className="redline-title">{suggestion.title}</span>
                  <span className="redline-status-tag">
                    {suggestion.status === "accepted"
                      ? "Accepted"
                      : suggestion.status === "ignored"
                        ? "Ignored"
                        : suggestion.status === "missing"
                          ? "Not found"
                          : "Pending"}
                  </span>
                </button>
                {expandedRedlineId === suggestion.id && (
                  <div className="redline-detail">
                    <div className="redline-diff">
                      <span className="diff-old">{suggestion.originalText}</span>
                      <span className="diff-new">{suggestion.suggestedText}</span>
                    </div>
                    <p className="redline-reason">{suggestion.reason}</p>
                    {suggestion.status === "pending" && (
                      <div className="redline-actions">
                        <button type="button" className="ghost-btn" onClick={() => onIgnoreRedline(suggestion)}>
                          Ignore
                        </button>
                        <button type="button" className="primary-btn" onClick={() => onAcceptRedline(suggestion)}>
                          Accept
                        </button>
                      </div>
                    )}
                    {suggestion.status === "missing" && (
                      <p className="redline-warning">
                        Original text could not be located in the document. It may have been edited since the suggestion was generated.
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {error && errorTab === activeTab && <p className="error">{error}</p>}
      </main>

      {activeTab === "review" && (
        <>
          <div className="composer-area">
            <div className="composer review-composer">
              <button
                type="button"
                className="review-composer-tool"
                title="Prompt library"
                aria-label="Open prompt library"
                disabled={isReplying}
                onClick={() => setIsReviewPromptLibraryOpen(true)}
              >
                <IconBookOpen />
              </button>
              <textarea
                ref={textareaRef}
                placeholder="Ask Jurion to review or research legal text…"
                value={prompt}
                onChange={(event) => setPrompt(event.target.value)}
                onKeyDown={onComposerKeyDown}
                rows={2}
              />
              <button
                type="button"
                className="send-btn"
                disabled={!prompt.trim() || isReplying}
                onClick={onSendPrompt}
                aria-label="Send"
              >
                <IconSend />
              </button>
            </div>
            <div className="status-bar">
              <span className="kbd-hint">↩ to send · ⇧↩ for new line</span>
              <span className={`doc-status ${docLinked ? "linked" : "unlinked"}`}>
                <span className="dot" />
                {docLinked ? "Document linked" : "No document"}
              </span>
            </div>
          </div>
          <PromptLibraryDialog
            isOpen={isReviewPromptLibraryOpen}
            onClose={() => setIsReviewPromptLibraryOpen(false)}
            onSelectPrompt={(text) => {
              setPrompt((prev) => (prev.trim().length > 0 ? `${prev}\n${text}` : text));
              setIsReviewPromptLibraryOpen(false);
            }}
          />
        </>
      )}
    </div>
  );
}
